/**
 * @fileoverview JavaScript port of https://github.com/azmenak/material-ui-nested-menu-item
 * Original license: MIT
 *
 * This version contains bug fixes for:
 * - Scrolling of nested menus
 *  - from https://github.com/azmenak/material-ui-nested-menu-item/pull/24
 * - Improved arror key navigation
 *  - from https://codesandbox.io/s/material-ui-nested-menu-v2-s7szu?file=/src/NestedMenuItem.js
 *
 * MUI PR for nested menus is still forthcoming: https://github.com/mui/material-ui/issues/11723
 * When merged, the implementation will probably support triangulation, a usability feature that
 * avoids closing the menu when moving between parent and child menus.
 */

import React, { useState, useRef, useImperativeHandle } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowRight from '@mui/icons-material/ArrowRight';

const TRANSPARENT = 'rgba(0,0,0,0)';

/**
 * Use as a drop-in replacement for `<MenuItem>` when you need to add cascading
 * menu elements as children to this component.
 */
const NestedMenuItem = React.forwardRef((props, ref) => {
    const {
        parentMenuOpen,
        component = 'div',
        label,
        rightIcon = <ArrowRight />,
        children,
        className,
        tabIndex: tabIndexProp,
        MenuProps = {},
        ContainerProps: ContainerPropsProp = {},
        ...MenuItemProps
    } = props;

    const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

    const menuItemRef = useRef(null);
    useImperativeHandle(ref, () => menuItemRef.current);

    const menuContainerRef = useRef(null);

    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const handleMouseEnter = (event) => {
        setIsSubMenuOpen(true);

        if (ContainerProps?.onMouseEnter) {
            ContainerProps.onMouseEnter(event);
        }
    };
    const handleMouseLeave = (event) => {
        setIsSubMenuOpen(false);

        if (ContainerProps?.onMouseLeave) {
            ContainerProps.onMouseLeave(event);
        }
    };

    // Check if any immediate children are active
    const isSubmenuFocused = () => {
        const active = menuItemRef.current?.ownerDocument?.activeElement;
        for (const child of menuContainerRef.current?.children ?? []) {
            if (child === active) {
                return true;
            }
        }
        return false;
    };

    const handleFocus = (event) => {
        if (event.target === menuItemRef.current) {
            setIsSubMenuOpen(true);
        }

        if (ContainerProps?.onFocus) {
            ContainerProps.onFocus(event);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            return;
        }

        if (isSubmenuFocused()) {
            event.stopPropagation();
        }

        const length = menuContainerRef.current?.children.length;
        if (length && length > 0) {
            if (event.target === menuContainerRef.current?.children[length - 1] && event.key === 'ArrowDown') {
                menuContainerRef.current.children[0].focus();
                event.stopPropagation();
            } else if (event.target === menuContainerRef.current?.children[0] && event.key === 'ArrowUp') {
                menuContainerRef.current.children[length - 1].focus();
                event.stopPropagation();
            }
        }

        const active = menuItemRef.current?.ownerDocument?.activeElement;

        if (event.key === 'ArrowLeft') {
            menuItemRef.current?.focus();
            setIsSubMenuOpen(false);
        }

        if (
            event.key === 'ArrowRight'
            && event.target === menuItemRef.current
            && event.target === active
        ) {
            if (isSubMenuOpen) {
                const firstChild = menuContainerRef.current?.children[0];
                firstChild?.focus();
            } else {
                handleFocus(event);
            }
        }

        if (
            (event.key === 'ArrowDown' || event.key === 'ArrowUp')
            && event.target === menuItemRef.current
        ) {
            setIsSubMenuOpen(false);
        }
    };

    const open = isSubMenuOpen && parentMenuOpen;

    // Root element must have a `tabIndex` attribute for keyboard navigation
    let tabIndex;
    if (!props.disabled) {
        tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
    }

    return (
        <MenuItem
            {...ContainerProps}
            onFocus={handleFocus}
            tabIndex={tabIndex}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
            role="menuitem"
            {...MenuItemProps}
            sx={(theme) => ({
                backgroundColor: props.open ? theme.palette.action.hover : TRANSPARENT,
                '& .MuiGrid-root': {
                    width: '100%',
                },
            })}
            ref={menuItemRef}
        >
            {label}
            {rightIcon}
            <Menu
        // Set pointer events to 'none' to prevent the invisible Popover div
        // from capturing events for clicks and hovers
                style={{ pointerEvents: 'none', overflow: 'none' }}
                PaperProps={{
                    style: { pointerEvents: 'auto' },
                }}
                anchorEl={menuItemRef.current}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                open={open}
                // autoFocus={false}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                onClose={() => {
                    setIsSubMenuOpen(false);
                }}
                {...MenuProps}
            >
                <div ref={menuContainerRef}>
                    {children}
                </div>
            </Menu>
        </MenuItem>
    );
});

export default NestedMenuItem;
